import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch } from 'react-redux';
import { store, AppDispatch } from './store';
import { MalPayThemeProvider } from './theme/index';
import { initializeAuth } from './store/slices/authSlice';

// Components
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import MerchantProtectedRoute from './components/Auth/MerchantProtectedRoute';
import AdminLayout from './components/Admin/AdminLayout';
import AdminProtectedRoute from './components/Admin/AdminProtectedRoute';

// Pages
import LoginPage from './pages/Auth/LoginPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/Auth/ResetPasswordPage';
import RegisterPage from './pages/Auth/RegisterPage';
import SetPasswordPage from './pages/Auth/SetPasswordPage';
import {
  EmailVerificationPage,
  ProfileCompletionPage,
  CardsPage,
  AddCardPage,
  SendMoneyPage,
  TransactionHistoryPage,
  BankAccountsPage,
  AddBankAccountPage,
  ProfilePage,
  SettingsPage,
  QRCodePage,
  NotFoundPage,
  TransactionDetailPage,
  KYCPageComponent,
  NotificationsPageComponent,
} from './pages';
import DashboardPage from './pages/Dashboard/DashboardPage';
import MerchantDashboardPage from './pages/Merchant/MerchantDashboardPage';
import PaymentLinksPage from './pages/Merchant/PaymentLinksPage';
import StoreManagementPage from './pages/Merchant/StoreManagementPage';
import MerchantTransactionsPage from './pages/Merchant/MerchantTransactionsPage';
import MerchantSettingsPage from './pages/Merchant/MerchantSettingsPage';
import MerchantApplicationPage from './pages/Merchant/MerchantApplicationPage';
import QRPayPage from './pages/QRPay/QRPayPage';
import SupportPage from './pages/Support/SupportPage';
import LandingPage from './pages/Landing/LandingPage';

// Admin Pages
import AdminLoginPage from './pages/Admin/AdminLoginPage';
import AdminDashboardPage from './pages/Admin/AdminDashboardPage';
import UserManagementPage from './pages/Admin/UserManagementPage';
import MerchantApprovalPage from './pages/Admin/MerchantApprovalPage';
import SupportTicketsPage from './pages/Admin/SupportTicketsPage';
import AdminEarningsPage from './pages/Admin/AdminEarningsPage';
import AdminSettingsPage from './pages/Admin/AdminSettingsPage';

const AppContent: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<EmailVerificationPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          
          {/* QR Pay - protected but standalone (no sidebar) */}
          <Route path="/pay" element={
            <ProtectedRoute>
              <QRPayPage />
            </ProtectedRoute>
          } />

          {/* Protected Routes (pathless layout: children resolve to /dashboard, /cards, ...) */}
          <Route element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="profile-completion" element={<ProfileCompletionPage />} />
            
            {/* Cards */}
            <Route path="cards" element={<CardsPage />} />
            <Route path="cards/add" element={<AddCardPage />} />
            
            {/* Payments */}
            <Route path="send-money" element={<SendMoneyPage />} />
            <Route path="transactions" element={<TransactionHistoryPage />} />
            
            {/* Bank Accounts */}
            <Route path="bank-accounts" element={<BankAccountsPage />} />
            <Route path="bank-accounts/add" element={<AddBankAccountPage />} />
            
            {/* Transaction Detail */}
            <Route path="transaction/:id" element={<TransactionDetailPage />} />
            
            {/* KYC */}
            <Route path="kyc" element={<KYCPageComponent />} />

            {/* Notifications */}
            <Route path="notifications" element={<NotificationsPageComponent />} />

            {/* Profile & Settings */}
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<SettingsPage />} />

            {/* QR Code */}
            <Route path="qr-code" element={<QRCodePage />} />
            <Route path="support" element={<SupportPage />} />

            {/* Merchant Application (accessible without merchant approval) */}
            <Route path="merchant/apply" element={<MerchantApplicationPage />} />

            {/* Merchant Routes (gated behind verification) */}
            <Route path="merchant/dashboard" element={<MerchantProtectedRoute><MerchantDashboardPage /></MerchantProtectedRoute>} />
            <Route path="merchant/payment-links" element={<MerchantProtectedRoute><PaymentLinksPage /></MerchantProtectedRoute>} />
            <Route path="merchant/store" element={<MerchantProtectedRoute><StoreManagementPage /></MerchantProtectedRoute>} />
            <Route path="merchant/transactions" element={<MerchantProtectedRoute><MerchantTransactionsPage /></MerchantProtectedRoute>} />
            <Route path="merchant/settings" element={<MerchantProtectedRoute><MerchantSettingsPage /></MerchantProtectedRoute>} />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin" element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />
            <Route path="users" element={<UserManagementPage />} />
            <Route path="merchants" element={<MerchantApprovalPage />} />
            <Route path="support" element={<SupportTicketsPage />} />
            <Route path="earnings" element={<AdminEarningsPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="system" element={<AdminSettingsPage />} />
          </Route>

          {/* 404 */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <MalPayThemeProvider>
        <AppContent />
      </MalPayThemeProvider>
    </Provider>
  );
}

export default App;